// apps/accounts/src/pages/partner/PartnerProjectDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  Users,
  Calendar,
  FileText,
  BarChart3,
  Loader2,
  Home,
  IndianRupee,
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';

// --- Currency helper � returns only the formatted number ------
const formatNumber = (amount) => {
  if (!amount && amount !== 0) return '0';
  return Number(amount).toLocaleString('en-IN');
};

// --- Small component to display amount with ? icon ------------
const Currency = ({ amount, className = '' }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`}>
    <IndianRupee className="w-4 h-4 inline-block" />
    {formatNumber(amount)}
  </span>
);

const formatDate = (dateString) => {
  if (!dateString) return '�';
  try {
    const d = new Date(dateString);
    if (isNaN(d)) return 'Invalid date';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

export default function PartnerProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const partnersRes = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });
        if (!partnersRes.length) {
          setError('Partner not found');
          setLoading(false);
          return;
        }
        const partner = partnersRes[0];

        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });
        const assignedSiteIds = assignments.map(a => a.expand?.site?.id).filter(Boolean);

        if (!assignedSiteIds.includes(id)) {
          setError('You do not have access to this project');
          setLoading(false);
          return;
        }

        const projectData = await pb.collection('accounts_projects').getOne(id, {
          expand: 'clientId',
        });
        setProject(projectData);

        const [
          expensesData,
          vendorPaymentsData,
          clientPaymentsData,
          investmentsData,
          allPartners,
        ] = await Promise.all([
          pb.collection('project_expenses').getFullList({
            filter: `project="${id}"`,
            sort: '-date',
          }),
          pb.collection('vendor_payments').getFullList({
            filter: `project="${id}"`,
            sort: '-paymentDate',
            expand: 'vendor',
          }),
          pb.collection('client_payments').getFullList({
            filter: `project="${id}"`,
            sort: '-paymentDate',
          }),
          pb.collection('investments').getFullList({
            filter: `project="${id}"`,
            sort: '-date',
            expand: 'partner',
          }),
          pb.collection('partners').getFullList({ sort: 'name' }),
        ]);

        setExpenses(expensesData);
        setVendorPayments(vendorPaymentsData);
        setClientPayments(clientPaymentsData);
        setInvestments(investmentsData);
        setPartners(allPartners);
      } catch (err) {
        console.error('Error fetching project details:', err);
        setError('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    if (user && id) fetchData();
  }, [user, id]);

  // --- Calculations ---------------------------------------------
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalVendorPayments = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalClientPayments = clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalDeposits = investments.filter(i => i.type === 'deposit').reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalWithdrawals = investments.filter(i => i.type === 'withdrawal').reduce((sum, i) => sum + (i.amount || 0), 0);

  const netInvestedCapital = totalDeposits - totalWithdrawals;
  const collectedAmount = totalClientPayments + netInvestedCapital;
  const projectCost = totalExpenses + totalVendorPayments;
  const balance = collectedAmount - projectCost;
  const profitLoss = balance - netInvestedCapital;

  // --- Investor Summary -----------------------------------------
  const investorSummary = {};
  investments.forEach(i => {
    if (!investorSummary[i.partner]) {
      const partner = partners.find(p => p.id === i.partner);
      investorSummary[i.partner] = {
        name: partner?.name || 'Unknown',
        deposits: 0,
        withdrawals: 0,
        net: 0,
      };
    }
    if (i.type === 'deposit') {
      investorSummary[i.partner].deposits += i.amount || 0;
    } else {
      investorSummary[i.partner].withdrawals += i.amount || 0;
    }
    investorSummary[i.partner].net = investorSummary[i.partner].deposits - investorSummary[i.partner].withdrawals;
  });

  // --- Helpers --------------------------------------------------
  const getStatusBadge = (status) => {
    const map = {
      'upcoming': { label: 'Upcoming', variant: 'info' },
      'ongoing': { label: 'Ongoing', variant: 'success' },
      'completed': { label: 'Completed', variant: 'success' },
      'on-hold': { label: 'On Hold', variant: 'warning' },
      'active': { label: 'Active', variant: 'success' },
      'planning': { label: 'Planning', variant: 'info' },
    };
    const s = map[status] || { label: status || 'N/A', variant: 'default' };
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  // --- Loading / Error ------------------------------------------
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
        <p className="text-slate-500 font-medium">Loading project details...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-800">{error || 'Project not found'}</h2>
        <Link to="/partner/projects" className="text-amber-600 hover:underline mt-4 block">
          ? Back to My Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* --- Header ---------------------------------------------- */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Link to="/partner/projects" className="text-sm text-amber-600 hover:underline flex items-center gap-1">
            <Home className="w-4 h-4" /> Back to My Projects
          </Link>
          <h1 className="text-2xl font-bold text-slate-800 mt-1">{project.name}</h1>
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <span>{project.type || 'Project'}</span>
            {getStatusBadge(project.status)}
            <span>{project.location || 'No location'}</span>
            <Badge variant="outline" className="ml-2">?? Read-only</Badge>
          </p>
        </div>
        <div className="text-xs text-slate-400 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          ??? View only
        </div>
      </div>

      {/* --- Overview Cards -------------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Collected Amount</p>
                <p className="text-xl font-bold text-emerald-600">
                  <Currency amount={collectedAmount} />
                </p>
                <p className="text-xs text-slate-400 mt-1">Revenue + Net Invested Capital</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Project Cost</p>
                <p className="text-xl font-bold text-rose-600">
                  <Currency amount={projectCost} />
                </p>
                <p className="text-xs text-slate-400 mt-1">Expenses + Vendor Payments</p>
              </div>
              <TrendingDown className="w-8 h-8 text-rose-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Balance</p>
                <p className={`text-xl font-bold ${balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  <Currency amount={balance} />
                </p>
                <p className="text-xs text-slate-400 mt-1">Collected Amount - Cost</p>
              </div>
              <Wallet className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 shadow-sm ${profitLoss >= 0 ? 'border-l-green-600' : 'border-l-red-600'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Profit / Loss</p>
                <p className={`text-xl font-bold ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {profitLoss >= 0 ? '+' : ''}<Currency amount={profitLoss} />
                </p>
                <p className="text-xs text-slate-400 mt-1">Balance - Net Invested Capital</p>
              </div>
              {profitLoss >= 0 ? (
                <TrendingUp className="w-8 h-8 text-green-500" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Fund Flow & Investor Summary ---------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-500" /> Fund Flow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">?? Total Client Payments</span>
              <span className="font-semibold text-emerald-600"><Currency amount={totalClientPayments} /></span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">?? Net Invested Capital</span>
              <span className="font-semibold text-amber-600"><Currency amount={netInvestedCapital} /></span>
            </div>
            <div className="flex justify-between items-center border-b pb-2">
              <span className="text-sm text-slate-600">?? Total Withdrawals</span>
              <span className="font-semibold text-red-600"><Currency amount={totalWithdrawals} /></span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t-2 border-slate-200">
              <span className="text-sm font-bold text-slate-700">Collected Amount</span>
              <span className="font-bold text-slate-800"><Currency amount={collectedAmount} /></span>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" /> Investor Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(investorSummary).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No investors</p>
            ) : (
              <div className="space-y-3">
                {Object.values(investorSummary).map((inv, idx) => (
                  <div key={idx} className="border-b pb-2 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{inv.name}</span>
                      <Badge variant={inv.net >= 0 ? 'success' : 'destructive'}>
                        {inv.net >= 0 ? 'Investor' : 'Due'}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-slate-500">Deposited</span>
                      <span className="text-emerald-600"><Currency amount={inv.deposits} /></span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Returned</span>
                      <span className="text-red-600"><Currency amount={inv.withdrawals} /></span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t">
                      <span>Net Balance</span>
                      <span className={inv.net >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        <Currency amount={inv.net} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* --- Transactions Tables ---------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-t-4 border-t-amber-400">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-amber-500" /> Project Expenses
          </CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">From</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {expenses.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No expenses</td></tr>
                  ) : (
                    expenses.map(e => (
                      <tr key={e.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(e.date)}</td>
                        <td className="px-4 py-2"><Badge className="bg-blue-100 text-blue-700">{e.category}</Badge></td>
                        <td className="px-4 py-2 text-xs">
                          {e.paidFrom === 'office_fund' ? '?? Office' : partners.find(p => p.id === e.paidFrom)?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-right font-mono"><Currency amount={e.amount} /></td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr><td colSpan="3" className="px-4 py-2 text-right">Total</td><td className="px-4 py-2 text-right"><Currency amount={totalExpenses} /></td></tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-blue-400">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-500" /> Vendor Payments
          </CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Vendor</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">From</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {vendorPayments.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No vendor payments</td></tr>
                  ) : (
                    vendorPayments.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                        <td className="px-4 py-2">{p.expand?.vendor?.name || 'N/A'}</td>
                        <td className="px-4 py-2 text-xs">
                          {p.paidFrom === 'office_fund' ? '?? Office' : partners.find(pr => pr.id === p.paidFrom)?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-right font-mono"><Currency amount={p.amount} /></td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr><td colSpan="3" className="px-4 py-2 text-right">Total</td><td className="px-4 py-2 text-right"><Currency amount={totalVendorPayments} /></td></tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Client Payments Table ------------------------------ */}
      <Card className="border-t-4 border-t-emerald-400">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" /> Client Payments Received
        </CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Method</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clientPayments.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-4 text-center text-slate-500">No client payments</td></tr>
                ) : (
                  clientPayments.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                      <td className="px-4 py-2"><Badge className="bg-green-100 text-green-700">{p.method}</Badge></td>
                      <td className="px-4 py-2">{p.note || '-'}</td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-600"><Currency amount={p.amount} /></td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t font-semibold">
                <tr><td colSpan="3" className="px-4 py-2 text-right">Total Revenue</td><td className="px-4 py-2 text-right"><Currency amount={totalClientPayments} /></td></tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* --- Investments Table ------------------------------------ */}
      <Card className="border-t-4 border-t-purple-400">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-purple-500" /> Investments
        </CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Partner</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {investments.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-4 text-center text-slate-500">No investments</td></tr>
                ) : (
                  investments.map(inv => {
                    const partner = partners.find(p => p.id === inv.partner);
                    return (
                      <tr key={inv.id} className="hover:bg-slate-50/50">
                        <td className="px-4 py-2">{formatDate(inv.date)}</td>
                        <td className="px-4 py-2">{partner?.name || 'N/A'}</td>
                        <td className="px-4 py-2">
                          <Badge variant={inv.type === 'deposit' ? 'success' : 'destructive'}>
                            {inv.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2">{inv.note || '-'}</td>
                        <td className={`px-4 py-2 text-right font-mono ${inv.type === 'deposit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {inv.type === 'deposit' ? '+' : '-'}<Currency amount={inv.amount} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t font-semibold">
                <tr>
                  <td colSpan="4" className="px-4 py-2 text-right">Net Invested Capital</td>
                  <td className="px-4 py-2 text-right"><Currency amount={netInvestedCapital} /></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* --- Footer ---------------------------------------------- */}
      <div className="text-center text-xs text-slate-400 pt-4 border-t">
        <p>??? Read-only view � You cannot add or edit any data</p>
        <p className="mt-1">Project ID: {project.id}</p>
      </div>
    </div>
  );
}